<script>
// @ts-check

import { getVatReport } from '@/api/reports'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import FileViewer from '@/components/file/FileViewer.vue'
import { demoMixin } from '@/core/mixins/demo'
import { formattedPeriod } from '@/helpers/formatters'
import { downloadFile } from '@/helpers/functions'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'VatReports',
  components: {
    FileViewer,
    LoadingSpinner
  },
  mixins: [demoMixin],
  data: () => ({
    /** @type {?number} */
    vatPeriodId: null,
    /** @type {?FileResource} */
    report: null,
    originalVatPeriods: null,
    isLoadingPeriods: false,
    isLoadingReport: false
  }),
  computed: {
    ...mapState('companyData', ['company']),
    ...mapGetters('mobileData', ['isMobileWrapperUser']),
    ...mapGetters('reports', ['vatPeriods']),
    ...mapGetters('app', ['isTlpService', 'isTmiService']),
    ...mapGetters('companyData', ['isSuomifiAuthorized', 'isVatLiable']),
    formattedVatPeriodOptions() {
      return this.vatPeriods.map(formattedPeriod)
    },
    formattedStartDate() {
      const vatPeriodData = this.vatPeriods.find(
        ({ id }) => id == this.vatPeriodId
      )
      return vatPeriodData?.period_start
    },
    suomiFiFaqLink() {
      if (this.isTmiService) {
        return this.$t('alert.missing_suomi_fi_authorization.faq_link_tmi')
      }

      return this.$t('alert.missing_suomi_fi_authorization.faq_link_other')
    }
  },
  watch: {
    async vatPeriodId(periodId) {
      if (!periodId) {
        return
      }

      await this.fetchVatReport()
    }
  },
  async created() {
    await this.skipInDemoMode(this.fetchVatPeriods)
  },
  methods: {
    ...mapActions('reports', ['getVatPeriods']),
    async fetchVatPeriods() {
      this.isLoadingPeriods = true

      await this.getVatPeriods()

      const vatPeriod = this.vatPeriods.find(({ current }) => current === true)

      if (vatPeriod) {
        this.vatPeriodId = vatPeriod.id
      }

      this.isLoadingPeriods = false
    },
    async fetchVatReport() {
      if (!this.vatPeriodId) {
        return
      }

      this.report = null
      this.isLoadingReport = true

      this.report = await getVatReport(this.vatPeriodId)

      this.isLoadingReport = false
    },
    downloadReport() {
      if (!this.report) {
        return
      }

      downloadFile(
        this.report.file,
        `${this.formattedStartDate.replace(/-/g, '')}_vat_report.pdf`,
        'application/pdf',
        this.isMobileWrapperUser
      )
    }
  }
}
</script>

<template>
  <div v-if="isLoadingPeriods" class="flex items-center justify-center">
    <p>{{ $t('reports.loading_vat_periods') }}</p>
    <LoadingSpinner class="ml-4" />
  </div>

  <div v-else class="grid-content">
    <template v-if="!isTlpService && !isInDemoMode">
      <template v-if="isSuomifiAuthorized">
        <p>
          {{
            isVatLiable
              ? $t('reports.vat_report.vat_period_info')
              : $t('reports.vat_report.non_vat_liable_vat_period_info')
          }}
        </p>
        <p>{{ $t('reports.vat_report.vat_period_change') }}</p>
      </template>
      <template v-else>
        <p>
          {{ $t('reports.vat_report.missing_suomifi_authorization') }}
        </p>
        <p>
          {{ $t('reports.vat_report.suomifi_instructions') }}
          <a
            :href="suomiFiFaqLink"
            target="_blank"
            rel="noopener noreferrer"
            class="block text-primary underline"
            >{{ $t('alert.missing_suomi_fi_authorization.faq_link') }}</a
          >
        </p>
      </template>
    </template>

    <div class="grid-content">
      <h3 class="heading-3">
        {{ $t('reports.vat_report.vat_details.heading') }}
      </h3>
      <table>
        <tbody>
          <tr>
            <th class="pr-3 text-left align-top">
              {{ $t('reports.vat_report.vat_period_length.heading') }}
            </th>
            <td class="flex items-center">
              <div>
                {{
                  isSuomifiAuthorized
                    ? $t(
                        `reports.vat_report.vat_period_length.option.${company.vat_period_length}`
                      )
                    : $t(
                        'reports.vat_report.vat_period_length.option.not_known'
                      )
                }}
              </div>
              <VIcon
                v-if="!isSuomifiAuthorized"
                v-tooltip="$t('reports.vat_report.tooltip_no_authorization')"
                icon="$information-outline"
                class="ml-2 block fill-current text-primary focus:outline-hidden"
                size="small"
              />
            </td>
          </tr>
          <tr>
            <th class="pr-3 text-left align-top">
              {{ $t('reports.vat_report.vat_liable.heading') }}
            </th>
            <td>
              {{
                isSuomifiAuthorized
                  ? $t(`reports.vat_report.vat_liable.option.${isVatLiable}`)
                  : $t('reports.vat_report.vat_liable.option.not_known')
              }}
            </td>
          </tr>
          <tr>
            <th class="pr-3 text-left align-top">
              {{
                $t('reports.vat_report.has_given_suomifi_authorization.heading')
              }}
            </th>
            <td>
              {{
                $t(
                  `reports.vat_report.has_given_suomifi_authorization.option.${isSuomifiAuthorized}`
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <VSelect
      v-model="vatPeriodId"
      :label="$t('form.label.vat_period')"
      :placeholder="$t('reports.choose_vat_period')"
      :items="formattedVatPeriodOptions"
      item-title="label"
      item-value="value"
    />

    <FileViewer v-if="report" class="w-full" :file="report" />

    <div v-else-if="isLoadingReport" class="flex items-center justify-center">
      <p>{{ $t('reports.loading_vat_report') }}</p>
      <LoadingSpinner class="ml-4" />
    </div>

    <div v-if="!isInDemoMode" class="justify-end sm:flex">
      <PrimaryButton
        v-show="report"
        class="w-full lg:w-auto"
        :text="$t('reports.download_vat_report')"
        @click="downloadReport"
      />
    </div>
  </div>
</template>
